// src/features/reception/QuickBookingPage.tsx
// Single-page quick booking form for reception staff
// - Phone lookup for returning patients (auto-fill)
// - All booking fields on one page (department, doctor, date, session, patient)

import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, CheckCircle, UserCheck, User,
  Calendar, Clock, Phone, Stethoscope, Building2,
  Sun, Moon, AlertCircle, RotateCcw,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  receptionService, departmentService, doctorService,
} from '../../api/services';
import type {
  DepartmentDto, DoctorListDto, AvailableSlotDto,
  CreateAppointmentRequest, PatientLookupDto,
} from '../../api/types';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { statusLabel } from '../../utils/statusLabel';

/* ═══════════════════════════════════════════════════════════════════ */

export function QuickBookingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Patient lookup ──────────────────────────────────────────────
  const [phoneInput, setPhoneInput] = useState('');
  const [lookupPhone, setLookupPhone] = useState<string | null>(null);
  const [patientData, setPatientData] = useState<PatientLookupDto | null>(null);
  const [lookupDone, setLookupDone] = useState(false);

  const { isFetching: lookingUp } = useQuery({
    queryKey: ['patient-lookup', lookupPhone],
    queryFn: async () => {
      try {
        const data = await receptionService.patientLookup(lookupPhone!);
        setPatientData(data);
        setForm((prev) => ({
          ...prev,
          patientName: data.name,
          patientPhone: data.phone,
          patientGender: data.gender ?? '',
          patientEmail: data.email ?? '',
        }));
        toast.success(`Returning patient — ${data.appointmentCount} previous appointment(s)`);
      } catch {
        setPatientData(null);
        setForm((prev) => ({ ...prev, patientPhone: lookupPhone! }));
        toast('First-time appointment — please fill in details', { icon: '📝' });
      }
      setLookupDone(true);
      return null;
    },
    enabled: !!lookupPhone,
  });

  const handleLookup = () => {
    const cleaned = phoneInput.replace(/\D/g, '');
    if (cleaned.length === 10 && /^[6-9]/.test(cleaned)) {
      setLookupPhone(cleaned);
      setLookupDone(false);
      setPatientData(null);
    } else {
      toast.error('Enter a valid 10-digit Indian mobile number');
    }
  };

  // ── Booking form state ──────────────────────────────────────────
  const [form, setForm] = useState({
    departmentId: 0,
    doctorId: 0,
    appointmentDate: format(new Date(), 'yyyy-MM-dd'),
    sessionType: '',
    bookingSource: 'Phone Call',
    patientName: '',
    patientPhone: '',
    patientGender: '',
    patientEmail: '',
    patientNotes: '',
  });

  const set = (key: string, value: string | number) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // ── Fetch departments ───────────────────────────────────────────
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: departmentService.getAll,
  });

  // ── Fetch doctors (filtered by department) ──────────────────────
  const { data: doctors = [], isLoading: doctorsLoading } = useQuery({
    queryKey: ['doctors', form.departmentId],
    queryFn: () => doctorService.getAll({
      departmentId: form.departmentId || undefined,
      acceptingBookings: true,
    }),
    enabled: form.departmentId > 0,
  });

  // Reset doctor when department changes
  useEffect(() => {
    setForm((prev) => ({ ...prev, doctorId: 0, sessionType: '' }));
  }, [form.departmentId]);

  // ── Fetch available slots for selected doctor ───────────────────
  const { data: slots = [], isLoading: slotsLoading } = useQuery({
    queryKey: ['doctor-slots', form.doctorId],
    queryFn: () => doctorService.getAvailableSlots(form.doctorId),
    enabled: form.doctorId > 0,
  });

  // Selected doctor info
  const selectedDoctor = useMemo(
    () => doctors.find((d) => d.id === form.doctorId),
    [doctors, form.doctorId]
  );

  // Group slots by date
  const slotsByDate = useMemo(() => {
    const grouped: Record<string, AvailableSlotDto[]> = {};
    slots.forEach((slot) => {
      (grouped[slot.slotDate] ??= []).push(slot);
    });
    return grouped;
  }, [slots]);

  // Available dates
  const availableDates = useMemo(() => Object.keys(slotsByDate).sort(), [slotsByDate]);

  // Sessions for selected date
  const sessionsForDate = useMemo(() => {
    if (!form.appointmentDate || !slotsByDate[form.appointmentDate]) return [];
    // Always show Morning before Evening, regardless of backend order
    const order: Record<string, number> = { Morning: 0, Evening: 1 };
    return [...slotsByDate[form.appointmentDate]].sort(
      (a, b) => (order[a.sessionType] ?? 99) - (order[b.sessionType] ?? 99)
    );
  }, [slotsByDate, form.appointmentDate]);

  // Reset session when date changes
  useEffect(() => {
    setForm((prev) => ({ ...prev, sessionType: '' }));
  }, [form.appointmentDate]);

  // Selected slot details
  const selectedSlot = useMemo(
    () => sessionsForDate.find((s) => s.sessionType === form.sessionType),
    [sessionsForDate, form.sessionType]
  );

  // ── Confirmation state ──────────────────────────────────────────
  const [confirmed, setConfirmed] = useState<any>(null);

  // ── Submit ──────────────────────────────────────────────────────
  const { mutate: submit, isPending: submitting } = useMutation({
    mutationFn: () => {
      const req: CreateAppointmentRequest = {
        doctorId: form.doctorId,
        departmentId: form.departmentId,
        appointmentDate: selectedDoctor?.isOnCall && !form.appointmentDate ? null : form.appointmentDate,
        sessionType: selectedDoctor?.isOnCall && !form.sessionType ? null : form.sessionType,
        patientName: form.patientName,
        patientPhone: form.patientPhone,
        patientGender: form.patientGender || undefined,
        patientEmail: form.patientEmail || undefined,
        patientNotes: form.patientNotes || undefined,
        bookingSource: form.bookingSource,
      };
      return receptionService.bookForPatient(req);
    },
    onSuccess: (data) => {
      setConfirmed(data);
      queryClient.invalidateQueries({ queryKey: ['reception-queue'] });
      toast.success(
        data?.tokenNumber
          ? `Booked! Token #${data.tokenNumber}`
          : 'Booked! Token pending confirmation.'
      );
    },
    onError: (err: any) => toast.error(err.message || 'Booking failed'),
  });

  const canSubmit =
    form.doctorId > 0 &&
    form.patientName.trim() &&
    form.patientPhone.length === 10 &&
    (selectedDoctor?.isOnCall || (form.appointmentDate && form.sessionType));

  // ── Reset ───────────────────────────────────────────────────────
  const reset = () => {
    setPhoneInput('');
    setLookupPhone(null);
    setPatientData(null);
    setLookupDone(false);
    setConfirmed(null);
    setForm({
      departmentId: 0, doctorId: 0,
      appointmentDate: format(new Date(), 'yyyy-MM-dd'),
      sessionType: '', bookingSource: 'Phone Call',
      patientName: '', patientPhone: '', patientGender: '', patientEmail: '', patientNotes: '',
    });
  };

  /* ═══════════════════════════════ RENDER ═══════════════════════════ */

  // ── Confirmation view ───────────────────────────────────────────
  if (confirmed) {
    return (
      <div className="max-w-lg mx-auto p-6 animate-fade-up">
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-emerald-600" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Appointment Confirmed!</h1>
          {confirmed.tokenNumber ? (
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-5 py-2 rounded-full text-lg font-bold mt-3">
              Token #{confirmed.tokenNumber}
            </div>
          ) : (
            <p className="text-sm text-amber-600 font-medium mt-2">Token will be assigned on date confirmation (on-call doctor)</p>
          )}
        </div>

        <div className="card p-5 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="col-span-2">
              <div className="label">Patient</div>
              <div className="font-semibold text-slate-900">{confirmed.patientName}</div>
              <div className="text-xs text-slate-500">{confirmed.patientPhone}</div>
            </div>
            <div>
              <div className="label">Appointment #</div>
              <div className="font-mono font-semibold text-brand-700">{confirmed.appointmentNumber}</div>
            </div>
            <div>
              <div className="label">Status</div>
              <span className={`status-${confirmed.status}`}>{statusLabel(confirmed.status)}</span>
            </div>
            <div>
              <div className="label">Doctor</div>
              <div className="font-medium text-slate-800">{confirmed.doctorName}</div>
            </div>
            <div>
              <div className="label">Department</div>
              <div className="font-medium text-slate-800">{confirmed.departmentName}</div>
            </div>
            <div>
              <div className="label">Date</div>
              <div className="font-medium text-slate-800">
                {confirmed.appointmentDate
                  ? format(parseISO(confirmed.appointmentDate), 'EEE, MMM d yyyy')
                  : <span className="text-amber-600">To be confirmed</span>}
              </div>
            </div>
            <div>
              <div className="label">Session</div>
              <div className="font-medium text-slate-800">
                {confirmed.sessionType ?? <span className="text-amber-600">TBC</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={() => navigate('/reception/queue')} className="btn btn-secondary flex-1 justify-center">
            View Queue
          </button>
          <button onClick={reset} className="btn btn-primary flex-1 justify-center">
            Book Another
          </button>
        </div>
      </div>
    );
  }

  // ── Main form ───────────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Quick Booking</h1>
          <p className="text-sm text-slate-500 mt-1">Book an appointment for a patient — all fields on one page</p>
        </div>
        <button onClick={reset} className="btn btn-ghost btn-sm">
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
        </button>
      </div>

      {/* ── 1. Phone Lookup ────────────────────────────────────────── */}
      <div className="card p-5 mb-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Phone className="w-4 h-4 text-brand-600" /> Patient Phone Lookup
        </h2>
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <input
              type="tel"
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
              className="input w-full pl-9"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            />
            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          </div>
          <button onClick={handleLookup} disabled={lookingUp} className="btn btn-primary">
            {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Search</span>
          </button>
        </div>

        {lookupDone && (
          <div className={clsx(
            'mt-3 px-3 py-2 rounded-lg text-sm flex items-center gap-2',
            patientData ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-blue-50 text-blue-700 border border-blue-200'
          )}>
            {patientData ? (
              <>
                <UserCheck className="w-4 h-4" />
                <span>
                  <strong>Returning patient</strong> — {patientData.name}
                  {patientData.appointmentCount > 0 && ` (${patientData.appointmentCount} previous visit${patientData.appointmentCount > 1 ? 's' : ''})`}
                </span>
              </>
            ) : (
              <>
                <User className="w-4 h-4" />
                <span><strong>First-time appointment</strong> — fill in details below</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── 2. Appointment Details ─────────────────────────────────── */}
      <div className="card p-5 mb-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-brand-600" /> Appointment Details
        </h2>

        {/* Booking Source */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Source:</span>
          {['Phone Call', 'WhatsApp', 'WalkIn'].map((src) => (
            <button
              key={src}
              type="button"
              onClick={() => set('bookingSource', src)}
              className={clsx(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
                form.bookingSource === src
                  ? 'bg-brand-600 text-white border-brand-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-brand-300 hover:text-brand-700'
              )}
            >
              {src === 'WalkIn' ? 'Walk-In' : src}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Department */}
          <div>
            <label className="label">Department *</label>
            <div className="relative">
              <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                className="input w-full pl-9"
                value={form.departmentId}
                onChange={(e) => set('departmentId', Number(e.target.value))}
              >
                <option value={0}>Select department...</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Doctor */}
          <div>
            <label className="label">Doctor *</label>
            <div className="relative">
              <Stethoscope className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                className="input w-full pl-9"
                value={form.doctorId}
                onChange={(e) => set('doctorId', Number(e.target.value))}
                disabled={!form.departmentId || doctorsLoading}
              >
                <option value={0}>
                  {!form.departmentId ? 'Select department first' : doctorsLoading ? 'Loading...' : 'Select doctor...'}
                </option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title ? `${d.title} ` : ''}{d.name}
                    {d.isOnCall ? ' (On-Call)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* On-call notice */}
          {selectedDoctor?.isOnCall && (
            <div className="md:col-span-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              On-call doctor — date and session are optional. Reception will confirm with doctor.
            </div>
          )}

          {/* Date */}
          {form.doctorId > 0 && (
            <div>
              <label className="label">Date {selectedDoctor?.isOnCall ? '' : '*'}</label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                {selectedDoctor?.isOnCall ? (
                  <input
                    type="date"
                    className="input w-full pl-9"
                    value={form.appointmentDate}
                    onChange={(e) => set('appointmentDate', e.target.value)}
                    min={format(new Date(), 'yyyy-MM-dd')}
                  />
                ) : slotsLoading ? (
                  <div className="input w-full pl-9 flex items-center text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading slots...
                  </div>
                ) : availableDates.length === 0 ? (
                  <div className="input w-full pl-9 flex items-center text-red-500 text-sm">
                    No available slots
                  </div>
                ) : (
                  <select
                    className="input w-full pl-9"
                    value={form.appointmentDate}
                    onChange={(e) => set('appointmentDate', e.target.value)}
                  >
                    <option value="">Select date...</option>
                    {availableDates.map((date) => (
                      <option key={date} value={date}>
                        {format(parseISO(date), 'EEE, MMM d yyyy')} ({slotsByDate[date].length} session{slotsByDate[date].length > 1 ? 's' : ''})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}

          {/* Session */}
          {form.doctorId > 0 && (
            <div>
              <label className="label">Session {selectedDoctor?.isOnCall ? '' : '*'}</label>
              <div className="flex gap-2">
                {selectedDoctor?.isOnCall ? (
                  // On-call: allow free selection
                  <>
                    {['Morning', 'Evening'].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => set('sessionType', form.sessionType === s ? '' : s)}
                        className={clsx(
                          'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all',
                          form.sessionType === s
                            ? 'border-brand-300 bg-brand-50 text-brand-700 ring-1 ring-brand-200'
                            : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                        )}
                      >
                        {s === 'Morning' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        {s}
                      </button>
                    ))}
                  </>
                ) : sessionsForDate.length === 0 ? (
                  <div className="text-sm text-slate-400 py-2">
                    {form.appointmentDate ? 'No sessions available for this date' : 'Select a date first'}
                  </div>
                ) : (
                  sessionsForDate.map((slot) => (
                    <button
                      key={slot.sessionType}
                      type="button"
                      onClick={() => set('sessionType', slot.sessionType)}
                      className={clsx(
                        'flex-1 flex flex-col items-center gap-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all',
                        form.sessionType === slot.sessionType
                          ? 'border-brand-300 bg-brand-50 text-brand-700 ring-1 ring-brand-200'
                          : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        {slot.sessionType === 'Morning' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        {slot.sessionType}
                      </span>
                      <span className="text-xs text-slate-400">{slot.startTime} – {slot.endTime}</span>
                      <span className="text-xs">
                        <span className={clsx(
                          slot.remainingSlots <= 3 ? 'text-red-500' : 'text-emerald-600'
                        )}>
                          {slot.remainingSlots} slot{slot.remainingSlots !== 1 ? 's' : ''} left
                        </span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── 3. Patient Details ─────────────────────────────────────── */}
      <div className="card p-5 mb-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <User className="w-4 h-4 text-brand-600" /> Patient Details
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Full Name *</label>
            <input
              className="input w-full"
              placeholder="Patient full name"
              value={form.patientName}
              onChange={(e) => set('patientName', e.target.value)}
            />
          </div>
          <div>
            <label className="label">Phone *</label>
            <input
              type="tel"
              className="input w-full"
              placeholder="10-digit mobile"
              maxLength={10}
              value={form.patientPhone}
              onChange={(e) => set('patientPhone', e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <div>
            <label className="label">Gender</label>
            <select className="input w-full" value={form.patientGender} onChange={(e) => set('patientGender', e.target.value)}>
              <option value="">Not specified</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              className="input w-full"
              placeholder="Optional"
              value={form.patientEmail}
              onChange={(e) => set('patientEmail', e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="label">Notes / Symptoms</label>
            <textarea
              className="input w-full"
              rows={2}
              placeholder="Reason for visit, symptoms, etc."
              value={form.patientNotes}
              onChange={(e) => set('patientNotes', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ── Submit ─────────────────────────────────────────────────── */}
      <button
        onClick={() => submit()}
        disabled={!canSubmit || submitting}
        className="btn btn-primary w-full py-3 text-base justify-center"
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
        ) : (
          <CheckCircle className="w-5 h-5 mr-2" />
        )}
        Book Appointment
      </button>
    </div>
  );
}
